import {
	FormItem,
	ReorderableList,
	Select,
	SelectItem,
	TextInput,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import {
	generateTrackingId,
	isNotNil
} from "client/Misc/utils";
import {
	ReactElement,
	useCallback,
	useMemo
} from "react";
import { localize } from "client_legacy/ndaw/culture";
import { never } from "client_legacy/ndaw/errors";
import {
	RECENT_FOLDER_REFERENCE,
	BOOKMARKS_FOLDER_REFERENCE
} from "./EditDocumentsTile";
import {
	SharePointUpdateDocumentsFolderModel,
	UpdateDocumentsAppTabModel,
	UpdateDocumentsFolderModel,
	WebDavUpdateDocumentsFolderModel
} from "client/Api/Workspace/workspace";
import { ItemPickerLocation } from "client/Features/Documents/ItemPicker/ItemPicker";
import { DocumentStoreType } from "client/Api/Documents/common";
import { useSelectDocumentsFolderDialog } from "client/Features/Documents/Dialogs/SelectDocumentsFolderDialog";
import { deepComparison } from "client/Misc/compareUtils";

const SELECT_OTHER_VALUE = "SelectOther";
type DocumentsFolderOptions = UpdateDocumentsFolderModel | typeof SELECT_OTHER_VALUE;

type EditDocumentsTabItemProps = {
	attachable: IValueInputAttachable<UpdateDocumentsAppTabModel>;
	onRemoveTab: () => void;
};

export const EditDocumentsTabItem = (props: EditDocumentsTabItemProps): ReactElement => {
	const { attachable, onRemoveTab } = props;

	const [selectDocumentFolderDialog, openSelectDocumentFolderDialog] = useSelectDocumentsFolderDialog();

	const folderAttachable: IValueInputAttachable<UpdateDocumentsFolderModel | null> = attachable.for("folder");

	const selectOptions: readonly SelectItem<DocumentsFolderOptions>[] = useMemo(
		() => {
			const options: SelectItem<DocumentsFolderOptions>[] = [
				{
					text: localize("_RecentlyModified"),
					value: RECENT_FOLDER_REFERENCE
				},
				{
					text: localize("_Bookmarks"),
					value: BOOKMARKS_FOLDER_REFERENCE
				}
			];

			if (
				folderAttachable.value
					&& (
						isWebDavFolder(folderAttachable.value)
						|| isSharePointFolder(folderAttachable.value)
				)
			) {
				options.push(
					{
						text: getNameFromSharepointOrWebDavFolderModel(folderAttachable.value),
						value: folderAttachable.value
					}
				);
			}

			options.push({ text: localize("_SelectOther"), value: SELECT_OTHER_VALUE });

			return options;
		},
		[folderAttachable.value]
	);

	const onSelectChangeHandler = useCallback(
		async (newValue: DocumentsFolderOptions): Promise<void> => {
			const onChange: (value: UpdateDocumentsFolderModel | null) => void = folderAttachable.attachToSelect()
				.onChange;
			if (newValue === SELECT_OTHER_VALUE ) {
				const newLocationResult: TryResult<ItemPickerLocation> = await openSelectDocumentFolderDialog({
					title: localize("_SelectAFolder"),
					options: {
						allowRootFolder: true,
						allowSharePoint: true,
						allowWebDav: true
					}
				});

				if (newLocationResult.wasSuccessful) {
					onChange(
						folderSelectionToUpdateDocumentsFolderModel(newLocationResult.result)
					);
				}
				return;
			}

			onChange(newValue);
		},
		[folderAttachable, openSelectDocumentFolderDialog]
	);

	return (
		<>
			<ReorderableList.PanelItem
				header={
					<Select
						{...folderAttachable.attachToSelect()}
						placeholder={localize("_PleaseSelect")}
						onChange={onSelectChangeHandler}
						options={selectOptions}
						menuTitle={localize("_Source")}
						areEqual={deepComparison}
					/>
				}
				trackingId={generateTrackingId("edit-document-tile-tab",`|${attachable.value.uniqueId}`)}
				onRemove={onRemoveTab}
			>
				<ValidationError {...folderAttachable.attachToValidationError()} />

				<FormItem
					label={localize("_Content")}
				>
					<TextInput
						{...attachable.for("name")
							.attachToTextInput()}
					/>
					<ValidationError
						{...attachable.for("name")
							.attachToValidationError()}
					/>
				</FormItem>
			</ReorderableList.PanelItem>
			{selectDocumentFolderDialog}
		</>
	);

};

EditDocumentsTabItem.displayName = "EditDocumentsTabItem";

function isSharePointFolder(
	folderModel: UpdateDocumentsFolderModel
): folderModel is SharePointUpdateDocumentsFolderModel {
	return isNotNil(
		(folderModel as SharePointUpdateDocumentsFolderModel).sharePoint
	);
}

function isWebDavFolder(
	folderModel: UpdateDocumentsFolderModel
): folderModel is WebDavUpdateDocumentsFolderModel {
	return isNotNil(
		(folderModel as WebDavUpdateDocumentsFolderModel).webDav
	);
}

function getNameFromSharepointOrWebDavFolderModel(
	folder: SharePointUpdateDocumentsFolderModel | WebDavUpdateDocumentsFolderModel
): string {
	if (isWebDavFolder(folder)) {
		return folder.webDav.name;
	}

	if (isSharePointFolder(folder)) {
		return folder.sharePoint.name;
	}

	never(folder);
}

function folderSelectionToUpdateDocumentsFolderModel(
	selection: ItemPickerLocation
): UpdateDocumentsFolderModel {

	switch (selection.kind) {
		case DocumentStoreType.WebDav: {
			const { folder: { id, name, server }} = selection;

			return {
				webDav: {
					id,
					name,
					serverId: server.id,
				},
			};
		}

		case DocumentStoreType.SharePoint: {
			const { folder: { id, name, location }} = selection;
			return {
				sharePoint: {
					siteUrl: location.siteUrl,
					listId: location.listId,
					id,
					name
				},
			};
		}

		default:
			never(selection);
	}
}
